/* eslint-disable jsx-a11y/control-has-associated-label */
import clsx from "clsx";
import { noop } from "lodash";
import PropTypes from "prop-types";
import React, { useRef, useState } from "react";
import Select, { components, createFilter } from "react-select";
import AsyncSelect from "react-select/async";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Checkbox } from "../Checkbox";
import { DeleteIcon } from "./DeleteIcon";
import "./SelectDropDown.scss";
import { customStyles } from "./styles";

const propTypes = {
  /**
   * Options containing value and label.
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.node, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
    })
  ),
  /**
   * Callback for when the option gets changed
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Default and disabled heading option for select
   */
  defaultLabel: PropTypes.string,
  /**
   * Boolean filed which set single/multiple selection on SelectDropDown
   */
  enableMulti: PropTypes.bool,
  /**
   * Default selected value for dropdown
   */
  defaultValue: PropTypes.shape({
    value: PropTypes.node,
    label: PropTypes.node,
  }),
  /**
   * Default and disabled heading option for select
   */
  placeholder: PropTypes.string,
  /**
   * Component Reference to call component method
   */
  externalRef: PropTypes.shape(),
  /**
   * Show options with checkbox
   */
  showCheckBoxOption: PropTypes.bool,

  /**
   * show selection unit or no
   */
  showUnit: PropTypes.bool,
  /**
   * Plural Unit
   */
  pluralUnit: PropTypes.string,
  /**
   * singular Unit
   */
  singularUnit: PropTypes.string,
  /**
   * If true, component will be disabled
   */
  isDisabled: PropTypes.bool,
  /**
   * Id of the component
   */
  id: PropTypes.string,
  /**
   * Selected option
   */
  value: PropTypes.shape({
    label: PropTypes.node,
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  }),
  async: PropTypes.bool,

  handleOnScrollBottom: PropTypes.func,
  loadOptions: PropTypes.func,
  page: PropTypes.number,
};

const defaultProps = {
  options: [],
  defaultLabel: "Select...",
  enableMulti: false,
  defaultValue: null,
  placeholder: "Selected option",
  externalRef: { current: {} },
  showCheckBoxOption: false,
  showUnit: false,
  pluralUnit: "Companies",
  singularUnit: "Company",
  isDisabled: false,
  id: undefined,
  value: undefined,
  async: false,
  page: 1,
  handleOnScrollBottom: () => {},
  loadOptions: (inputValue, callback) => callback([]),
};

const PERFECT_SCROLLBAR_OPTIONS = {
  wheelSpeed: 1,
  swipeEasing: true,
  wheelPropagation: false,
  minScrollbarLength: 60,
  scrollingThreshold: 1000,
};

// eslint-disable-next-line react/prop-types
function MenuList({ children, ...props }) {
  return (
    <components.MenuList {...props}>
      <PerfectScrollbar
        options={PERFECT_SCROLLBAR_OPTIONS}
        style={{ maxHeight: 235, padding: "5px 0" }}
      >
        {children}
      </PerfectScrollbar>
    </components.MenuList>
  );
}

// eslint-disable-next-line react/prop-types
export function Option({ children, ...props }) {
  // eslint-disable-next-line react/prop-types
  const { isSelected, label, data } = props;

  const onClick = (e) => {
    // eslint-disable-next-line react/prop-types
    props.selectOption({ ...data });
    e.stopPropagation();
    e.preventDefault();
  };
  return (
    <components.Option {...props}>
      <div role="presentation" onClick={onClick}>
        <Checkbox
          readOnly
          className="Uic-SelectDropDown-centerCB"
          checked={isSelected}
          label={label}
        />
      </div>
    </components.Option>
  );
}

/**
 * Select dropdown with options
 * Based on https://react-select.com/home.
 */
function SelectDropDown(props) {
  const {
    options,
    onChange,
    defaultLabel,
    enableMulti,
    defaultValue,
    placeholder,
    externalRef,
    showCheckBoxOption,
    showUnit,
    pluralUnit,
    singularUnit,
    isDisabled,
    id,
    value,
    async,
    handleOnScrollBottom,
    loadOptions,
    page,
    ...rest
  } = props;

  const internalRef = useRef();

  function clearValue() {
    internalRef.current.select.clearValue();
    internalRef.current.select.blur();
  }

  const [multiselectValue, setMultiselectValue] = useState(defaultValue);

  const onSelectChange = (selectedOption, actionMeta) => {
    if (
      selectedOption &&
      selectedOption.find((option) => option.value === "all")
    ) {
      setMultiselectValue(options);
      onChange(options);
    } else if (actionMeta.action === "clear") {
      setMultiselectValue([]);
      onChange([]);
    } else {
      setMultiselectValue(selectedOption);
      onChange(selectedOption);
    }
  };

  function MultiValueContainer() {
    return null;
  }

  externalRef.current.clearValue = clearValue;

  // eslint-disable-next-line react/no-unstable-nested-components
  function ValueContainer({
    // eslint-disable-next-line react/prop-types
    children,
    // eslint-disable-next-line no-shadow
    ...props
  }) {
    // eslint-disable-next-line prefer-const
    let [values, input] = children;
    let count = 0;

    if (Array.isArray(values)) {
      count = values.length;
      values = values.length === 1 ? placeholder : `${placeholder}(s)`;
    }

    return (
      <components.ValueContainer {...props}>
        {count > 0 && (
          <div
            className={clsx("Uic-SelectDropDown-count_section")}
            onClick={noop}
            onKeyDown={noop}
            role="button"
            tabIndex={0}
          >
            <div className={clsx("Uic-SelectDropDown-count")}>
              {count.toString()}
            </div>
            <div
              className={clsx("Uic-SelectDropDown-count_cross")}
              onClick={() => clearValue()}
              onKeyDown={noop}
              role="button"
              tabIndex={0}
            >
              <i className="fas fa-xmark" />
            </div>
          </div>
        )}
        <div onClick={noop} onKeyDown={noop} role="button" tabIndex={0}>
          {values}
        </div>
        {input}
      </components.ValueContainer>
    );
  }

  if (async) {
    return (
      <AsyncSelect
        {...rest}
        cacheOptions
        loadOptions={loadOptions}
        onMenuScrollToBottom={(e) => handleOnScrollBottom(e, page)}
        defaultOptions
        id={id}
        ref={internalRef}
        styles={customStyles}
        isMulti={enableMulti}
        isClearable={!enableMulti}
        isSearchable={!enableMulti}
        value={value}
        defaultValue={defaultValue || null}
        onChange={(selectedOption) => onChange(selectedOption)}
        placeholder={defaultLabel}
        isDisabled={isDisabled}
        components={{
          ValueContainer,
        }}
        filterOption={createFilter({
          matchFrom: "any",
          stringify: (option) => `${option.label}`,
        })}
      />
    );
  }
  return (
    <div>
      {!showCheckBoxOption ? (
        <Select
          id={id}
          ref={internalRef}
          styles={customStyles}
          options={options}
          isMulti={enableMulti}
          isClearable={!enableMulti}
          isSearchable={!enableMulti}
          value={value}
          defaultValue={defaultValue || null}
          onChange={(selectedOption) => onChange(selectedOption)}
          placeholder={defaultLabel}
          isDisabled={isDisabled}
          components={{
            ValueContainer,
          }}
          filterOption={createFilter({
            matchFrom: "any",
            stringify: (option) => `${option.label}`,
          })}
          {...rest}
        />
      ) : (
        <div>
          {multiselectValue.length > 0 && (
            <div className="Uic-SelectDropDown-wrapper">
              <div className="Uic-SelectDropDown-counter">
                {multiselectValue.length === 1
                  ? multiselectValue[0].label
                  : multiselectValue.length}
              </div>

              <DeleteIcon
                onClick={() => {
                  setMultiselectValue([]);
                  onChange([]);
                }}
              />
            </div>
          )}

          {showUnit && multiselectValue.length > 0 && (
            <div
              className="Uic-SelectDropDown-wrapperUnit"
              style={{ marginLeft: multiselectValue.length > 1 ? 57 : 54 }}
            >
              {multiselectValue.length > 1 ? pluralUnit : singularUnit}
            </div>
          )}

          <Select
            {...rest}
            id={id}
            ref={internalRef}
            value={multiselectValue}
            styles={customStyles}
            options={options}
            isMulti
            isSearchable={false}
            hideSelectedOptions={false}
            isClearable={false}
            closeMenuOnSelect={false}
            isDisabled={isDisabled}
            onChange={(selectedOption, actionMeta) =>
              onSelectChange(selectedOption, actionMeta)
            }
            components={{ MultiValueContainer, Option, MenuList }}
            placeholder={defaultLabel}
            filterOption={createFilter({
              matchFrom: "any",
              stringify: (option) => `${option.label}`,
            })}
          />
        </div>
      )}
    </div>
  );
}

SelectDropDown.propTypes = propTypes;
SelectDropDown.defaultProps = defaultProps;

export default SelectDropDown;
